import {
  MAKES_SEPARATOR,
  MAKE_MODELS_SEPARATOR,
  MULTI_MODELS_SEPARATOR,
  OLD_MAKES_SEPARATOR,
  OLD_MAKE_MODELS_SEPARATOR
} from './constants';

export function serializePairs(pairs = []) {
  return pairs
    .map(({ make, models = [] }) => {
      const modelsPart = models.length > 0 ? MAKE_MODELS_SEPARATOR + models.join(MULTI_MODELS_SEPARATOR) : '';
      return `${make}${modelsPart}`; // e.g. MERCEDES-BENZ.A-CLASS
    })
    .join(MAKES_SEPARATOR); // e.g. MERCEDES-BENZ.A-CLASS,C-CLASS_MITSUBISHI.TRITON
}

export function parsePairs(pairsStr) {
  if (!pairsStr) return [];
  const s = Array.isArray(pairsStr) ? pairsStr[0] : pairsStr;
  return s.split(MAKES_SEPARATOR).map((seg) => {
    const parts = seg.split(MAKE_MODELS_SEPARATOR, 2);
    const make = parts[0];
    const modelsRaw = parts[1] || '';
    const models = modelsRaw ? modelsRaw.split(MULTI_MODELS_SEPARATOR) : [];
    return { make, models };
  });
}

/**
 * Normalizes and canonicalizes the `pair` query parameter for backward compatibility.
 *
 * - Replaces old separators (OLD_MAKES_SEPARATOR, OLD_MAKE_MODELS_SEPARATOR)
 *   with the new ones (MAKES_SEPARATOR, MAKE_MODELS_SEPARATOR).
 * - Works with both string and array query param formats.
 * - Updates the provided `queryParams` object in-memory.
 * - If the URL contains old separators, it silently updates the browser URL
 *   using `$router.replace()` (no page reload and no new history entry).
 *
 * @param {Object} $route - Vue Router's current route object (this.$route)
 * @param {Object} $router - Vue Router instance (this.$router)
 * @param {Object} queryParams - A cloned copy of the current query parameters
 * @returns {Object} The updated (possibly normalized) query parameters
 */
export function normalizePairParam($route, $router, queryParams) {
  const normalizePair = (v) =>
    typeof v === 'string'
      ? v.replaceAll(OLD_MAKES_SEPARATOR, MAKES_SEPARATOR).replaceAll(OLD_MAKE_MODELS_SEPARATOR, MAKE_MODELS_SEPARATOR)
      : v;

  const originalPair = queryParams.pair;
  let normalizedPair = Array.isArray(originalPair) ? originalPair.map(normalizePair) : normalizePair(originalPair);

  // Merge multiple ?pair=... entries
  if (Array.isArray(normalizedPair)) {
    normalizedPair = normalizedPair.join(MAKES_SEPARATOR);
  }

  const changed =
    typeof originalPair === 'string'
      ? originalPair !== normalizedPair
      : Array.isArray(originalPair)
      ? JSON.stringify(originalPair) !== JSON.stringify(normalizedPair.split(MAKES_SEPARATOR))
      : false;

  if (changed) {
    // update local object
    queryParams = { ...$route.query, pair: normalizedPair };

    // silently update URL (no reload, no new history)
    $router.replace({ query: queryParams }).catch(() => {});
  }

  return queryParams;
}

/**
 * Shallow clone of `item` without `keys` (does not mutate `item`).
 * @param {Record<string, unknown>|null|undefined} item
 * @param {string[]} keys
 */
export function omitObjectKeys(item, keys) {
  if (!item || typeof item !== 'object') return item;
  const next = { ...item };
  keys.forEach((key) => {
    delete next[key];
  });
  return next;
}

export function isClosedOpportunityStage(stage) {
  if (typeof stage !== 'string') return false;

  const normalizedStageName = stage.toLowerCase().replace(/\s+/g, ' ').trim();

  const closedStages = ['closed won', 'closed lost', 'closedwon', 'closedlost'];

  return closedStages.some((keyword) => normalizedStageName.includes(keyword));
}

export async function waitUntil(fn, { retries = 20, delay = 100 } = {}) {
  for (let i = 0; i < retries; i++) {
    try {
      if (fn()) return true;
    } catch (e) {}
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, delay));
  }
  return false;
}
